(() => {
	const sel = {
		group: ".js-collection-group",
	};

	const MAX_CONCURRENT = 3;
	const queue = [];
	let active = 0;

	function enqueue(task) {
		queue.push(task);
		runNext();
	}

	function runNext() {
		if (active >= MAX_CONCURRENT) return;
		const next = queue.shift();
		if (!next) return;
		active++;
		next().finally(() => {
			active--;
			runNext();
		});
	}

	async function fetchFragment(url) {
		const res = await fetch(url, { credentials: "same-origin" });
		if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
		return res.text();
	}

	// snippets/bst-swatches.liquid (rendered with enable_image_preview: true) adds
	// data-image/-srcset/-alt to each swatch from that variant's featured image.
	// Swap the card's image to it on hover, and back on pointerleave. Preload all
	// of a card's swatch images as soon as the pointer enters the card, so the
	// swap is instant by the time a specific swatch is hovered.
	const preloadedImageUrls = new Set();

	function preloadImage(url, srcset) {
		if (!url || preloadedImageUrls.has(url)) return;
		preloadedImageUrls.add(url);

		const image = new Image();
		if (srcset) image.srcset = srcset;
		image.src = url;
	}

	function initSwatchImagePreview(container) {
		const cards = container.querySelectorAll(".bst-shop-all-card");

		cards.forEach(card => {
			const swatches = card.querySelectorAll(".bst-swatches__item[data-image]");
			const img = card.querySelector(".bst-shop-all-card__image");
			if (!swatches.length || !img) return;

			const originalSrc = img.currentSrc || img.src;
			const originalSrcset = img.srcset || "";
			const originalAlt = img.alt || "";

			card.addEventListener("pointerenter", () => {
				swatches.forEach(swatch => {
					preloadImage(
						swatch.getAttribute("data-image"),
						swatch.getAttribute("data-image-srcset"),
					);
				});
			});

			swatches.forEach(swatch => {
				swatch.addEventListener("pointerenter", () => {
					img.src = swatch.getAttribute("data-image");
					img.srcset = swatch.getAttribute("data-image-srcset") || "";
					img.alt = swatch.getAttribute("data-image-alt") || originalAlt;
				});

				swatch.addEventListener("pointerleave", () => {
					img.src = originalSrc;
					img.srcset = originalSrcset;
					img.alt = originalAlt;
				});
			});
		});
	}

	function initGroup(el) {
		if (el.__shopAllInit) return;
		el.__shopAllInit = true;

		const handle = el.getAttribute("data-collection-handle");
		const productCount = parseInt(
			el.getAttribute("data-product-count") || "0",
			10,
		);
		const initialPage = parseInt(
			el.getAttribute("data-initial-page") || "1",
			10,
		);

		// Skip loading if collection has no products
		if (productCount === 0) {
			return;
		}

		const state = {
			page: initialPage,
			loading: false,
			done: false,
		};

		const loadPage = page =>
			enqueue(async () => {
				if (state.loading || state.done) return;

				state.loading = true;
				el.setAttribute("data-loading", "true");
				const url = `/collections/${encodeURIComponent(
					handle,
				)}?view=shop-all-grid&page=${page}`;

				try {
					const html = await fetchFragment(url);
					const tmp = document.createElement("div");
					tmp.innerHTML = html;

					const fragment = tmp.querySelector(".shop-all-fragment");
					const grid = tmp.querySelector("[data-grid]");

					if (fragment) {
						const error = fragment.getAttribute("data-error");

						if (error) {
							// Show error message to user
							const errorDiv = document.createElement("div");
							errorDiv.className = "shop-all__error";
							errorDiv.innerHTML = `<p>Unable to load products for ${handle}. The collection may not exist.</p>`;
							el.appendChild(errorDiv);
						} else if (grid) {
							// Remove placeholder
							const ph = el.querySelector(".shop-all__placeholder");
							if (ph) ph.remove();

							// Append into the existing grid if one's already on the page
							// (future multi-page loads), otherwise adopt the fetched grid
							// element as-is so its tag/classes (e.g. <ul class="bst-shop-all-grid">)
							// carry over intact instead of being replaced by a generic wrapper.
							const existingGrid = el.querySelector("[data-grid]");
							if (existingGrid) {
								existingGrid.insertAdjacentHTML("beforeend", grid.innerHTML);
							} else {
								el.appendChild(grid.cloneNode(true));
							}

							// Yotpo only scans the DOM for `.yotpo` elements once, on initial
							// page load - these cards are inserted after that, so ask it to
							// re-scan. https://support.yotpo.com/docs/loading-yotpo-with-ajax
							window.yotpo?.initWidgets?.();
							window.yotpoWidgetsContainer?.initWidgets?.();

							initSwatchImagePreview(el);
						}
					}

					// Mark as done after first load
					state.done = true;
				} catch (e) {
					console.error("Shop All load error", e);
				} finally {
					state.loading = false;
					el.setAttribute("data-loading", "false");
				}
			});

		// Lazy load first page on intersection
		const io = new IntersectionObserver(
			entries => {
				entries.forEach(entry => {
					if (entry.isIntersecting) {
						loadPage(state.page);
						io.disconnect();
					}
				});
			},
			{ rootMargin: "200px 0px" },
		);
		io.observe(el);
	}

	function init() {
		const groups = document.querySelectorAll(sel.group);
		if (!groups.length) return;
		groups.forEach(initGroup);
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", init);
	} else {
		init();
	}
})();
